import React, { type ReactNode } from 'react'
import { WinningIcon } from '../duel/components/Winning/WinningIcon'
import { type WinningProps } from '../duel/components/Winning/WinningCard'
import { Card, colors } from 'app-v2/design-systems'
import styled from 'styled-components'
import { ConfettiDisplay } from '../duel/components/Confetti/Confetti'
import { TPKColors } from '../colors'
import { SPACING } from 'app-v2/design-tokens'
import { breakpoints } from 'app/assets/styles'

type CardWithWinningProps = {
    children: ReactNode,
    withConfettis?: boolean,
    withBorder?: boolean
} & WinningProps

export const CardWithWinning = ({
    children,
    type,
    withConfettis = false,
    withBorder = false,
    selected,
    ...winningProps
}: CardWithWinningProps) => {
    return (
        <StyledWinningCard
            type={type}
            selected={selected}
            withBorder={withBorder}>
            <StyledWinningIconContainer>
                {withConfettis && <ConfettiDisplay />}
                <StyledWinningIcon>
                    <WinningIcon
                        type={type}
                        selected={selected}
                        {...winningProps}
                    />
                </StyledWinningIcon>
                {withConfettis && <StyledShadow />}
            </StyledWinningIconContainer>

            <StyledContentContainer selected={selected}>
                {children}
            </StyledContentContainer>
        </StyledWinningCard>
    )
}

const StyledShadow = styled.div`
    position: absolute;
    top: -120px;
    width: 540px;
    height: 540px;
    background: radial-gradient(${colors.white}, transparent, transparent);
    z-index: 0;
`

const StyledContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: ${SPACING.L};
    box-sizing: border-box;
    z-index: 1;
    ${props => props.selected === false && 'opacity: 0.5;'}

    @media (max-width: ${breakpoints.small}px) {
        padding: ${SPACING.M};
    }
`

const StyledWinningCard = styled(Card)`
    position: relative;
    margin: ${({ withBorder, selected }) =>
        withBorder || selected ? 0 : '2px'};
    box-sizing: border-box;
    padding: 120px 0 0;
    width: 100%;
    height: 100%;
    background-blend-mode: normal, normal;
    background-color: ${colors.white};

    border: ${({ withBorder, selected, type }) =>
        withBorder &&
        selected &&
        `2px solid ${
            type === 'CHILD' ? TPKColors.childLight : TPKColors.parentLight
        }`};
    transition: margin 0.3s;

    @media (max-width: ${breakpoints.small}px) {
        gap: 16px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
        border-radius: 12px;
        padding-top: 60px;
    }
`

const StyledWinningIcon = styled.div`
    width: 240px;

    @media (max-width: ${breakpoints.small}px) {
        width: 120px;
    }
`

const StyledWinningIconContainer = styled.div`
    position: absolute;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    top: -120px;

    @media (max-width: ${breakpoints.small}px) {
        top: -60px;
    }
`
